import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { Plus, Search, Edit2, Trash2, UserCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import PersonaForm from "./PersonaForm";

type Persona = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  persona_prompt: string;
  avg_words_per_response: number;
  behavior_attributes: string[];
  speaking_style: string;
  interaction_style: string;
  technical_level: string;
  additional_config: any;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string | null;
};

interface PersonasListProps {
  onPersonaSelect?: (id: string) => void;
  selectedPersonaId?: string | null;
  className?: string | null;
}

export default function PersonasList({
  onPersonaSelect,
  selectedPersonaId: propSelectedPersonaId,
  className = "",
}: PersonasListProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [filteredPersonas, setFilteredPersonas] = useState<Persona[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(
    propSelectedPersonaId || null
  );
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingPersona, setDeletingPersona] = useState<Persona | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (user && currentOrganization) {
      fetchPersonas();
    }
  }, [user, currentOrganization]);

  useEffect(() => {
    if (!user || !currentOrganization) return;

    // Set up real-time subscription
    const subscription = supabase
      .channel(`personas-changes-${currentOrganization.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "personas",
          filter: `organization_id=eq.${currentOrganization.id}`,
        },
        () => fetchPersonas()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, currentOrganization]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredPersonas(personas);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredPersonas(
        personas.filter(
          (persona) =>
            persona.name.toLowerCase().includes(query) ||
            (persona.description &&
              persona.description.toLowerCase().includes(query)) ||
            persona.behavior_attributes.some(attr => 
              attr.toLowerCase().includes(query)
            )
        )
      );
    }
  }, [searchQuery, personas]);

  useEffect(() => {
    setSelectedId(propSelectedPersonaId || null);
  }, [propSelectedPersonaId]);

  const fetchPersonas = async () => {
    setIsLoading(true);
    try {
      if (!currentOrganization) {
        setPersonas([]);
        setFilteredPersonas([]);
        setIsLoading(false);
        return;
      }

      const { data: personasData, error: personasError } = await supabase
        .from("personas")
        .select("*")
        .eq("organization_id", currentOrganization.id)
        .order("created_at", { ascending: false });

      if (personasError) {
        console.error("Error fetching personas:", personasError);
        toast({
          title: "Error",
          description: "Failed to load personas. Please try again.",
          variant: "destructive",
        });
      } else if (personasData) {
        setPersonas(personasData);
        setFilteredPersonas(personasData);
      }
    } catch (error) {
      console.error("Error fetching personas:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading personas.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePersona = () => {
    setShowCreateDialog(true);
  };

  const handleEditPersona = (persona: Persona) => {
    setEditingPersona(persona);
    setShowEditDialog(true);
    if (onPersonaSelect) {
      onPersonaSelect(persona.id);
    }
    setSelectedId(persona.id);
  };

  const handleDeletePersona = (persona: Persona) => {
    setDeletingPersona(persona);
    setShowDeleteDialog(true);
  };

  const confirmDeletePersona = async () => {
    if (!deletingPersona) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("personas")
        .delete()
        .eq("id", deletingPersona.id);

      if (error) throw error;

      toast({
        title: "Persona Deleted",
        description: "The persona has been deleted successfully.",
      });

      setShowDeleteDialog(false);
      setDeletingPersona(null);
      fetchPersonas();
    } catch (error: any) {
      console.error("Error deleting persona:", error);
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete the persona.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowCreateDialog(false);
    setShowEditDialog(false);
    setEditingPersona(null);
    fetchPersonas();
  };

  const getBehaviorBadgeColor = (attribute: string) => {
    const colorMap: Record<string, string> = {
      'Polite': 'bg-good-bg text-good',
      'Aggressive': 'bg-danger-bg text-danger',
      'Patient': 'bg-secondary text-foreground',
      'Impatient': 'bg-mid-bg text-mid',
      'Friendly': 'bg-good-bg text-good',
      'Talkative': 'bg-secondary text-foreground',
      'Expert': 'bg-secondary text-foreground',
      'Beginner': 'bg-muted text-foreground',
    };
    return colorMap[attribute] || 'bg-muted text-foreground';
  };

  return (
    <div className={`${className} h-full flex flex-col`}>
      <div className="pr-6 py-6 space-y-4">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search personas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background border-border"
            />
          </div>
          <Button
            onClick={handleCreatePersona}
            className="bg-primary hover:bg-primary/80 text-primary-foreground font-medium"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Persona
          </Button>
        </div>

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {filteredPersonas.map((persona) => (
              <Card
                key={persona.id}
                className={`bg-card border-border hover:border-primary transition-colors cursor-pointer ${
                  selectedId === persona.id ? "border-primary" : ""
                }`}
                onClick={() => handleEditPersona(persona)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-base mb-2 text-primary">
                        {persona.name}
                      </h3>
                      {persona.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {persona.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-1 mb-2">
                        {persona.behavior_attributes.slice(0, 5).map((attr) => (
                          <span
                            key={attr}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getBehaviorBadgeColor(attr)}`}
                          >
                            {attr}
                          </span>
                        ))}
                        {persona.behavior_attributes.length > 5 && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-foreground">
                            +{persona.behavior_attributes.length - 5} more
                          </span>
                        )}
                      </div>

                      <div className="text-sm text-muted-foreground">
                        <div className="flex items-center gap-4">
                          <span>{persona.speaking_style} • {persona.interaction_style}</span>
                          <span>~{persona.avg_words_per_response} words</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPersona(persona);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePersona(persona);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {personas.length === 0 && (
              <div className="text-center py-12">
                <UserCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-base font-medium mb-2">
                  No personas found
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first persona to get started with simulation testing
                </p>
                <Button
                  onClick={handleCreatePersona}
                  className="bg-primary hover:bg-primary/80 text-primary-foreground font-medium text-sm w-full"
                >
                  Create First Persona
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Persona Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Persona</DialogTitle>
            <DialogDescription>
              Create a persona to simulate different types of users in testing scenarios.
            </DialogDescription>
          </DialogHeader>
          <PersonaForm 
            persona={null}
            onSuccess={handleFormSuccess}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Persona Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Persona</DialogTitle>
            <DialogDescription>
              Update the persona configuration and behavioral attributes.
            </DialogDescription>
          </DialogHeader>
          <PersonaForm 
            persona={editingPersona}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowEditDialog(false);
              setEditingPersona(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Persona</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingPersona?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeletingPersona(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeletePersona}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}